import { Link } from "react-router-dom";
import type { Report } from "../types";
import { formatDivergence, formatTimestamp } from "../lib/format";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { EvidenceBalanceBadge } from "./EvidenceBalanceBadge";
import { MarketContext } from "./MarketContext";
import { MarketSparkline } from "./MarketSparkline";
import { ProbabilityDivergenceBar } from "./ProbabilityDivergenceBar";

interface ReportCardProps {
  report: Report;
  variant: "discover" | "list" | "archive";
  isFeatured?: boolean;
}

export function ReportCard({ report, variant, isFeatured = false }: ReportCardProps) {
  const locked = report.accessTier === "pro" && !isFeatured;
  const ftmEstimateLabel = locked
    ? "Locked"
    : report.ftmEstimateLow !== null && report.ftmEstimateLow !== undefined && report.ftmEstimateHigh !== null && report.ftmEstimateHigh !== undefined
      ? `${Math.round(report.ftmEstimateLow * 100)}–${Math.round(report.ftmEstimateHigh * 100)}%`
      : "Unavailable";
  const divergenceLabel = locked
    ? "Pro"
    : report.divergenceLow !== null && report.divergenceLow !== undefined && report.divergenceHigh !== null && report.divergenceHigh !== undefined
      ? formatDivergence(report.divergenceLow, report.divergenceHigh)
      : "Unavailable";
  return (
    <article className={`report-card report-card-${variant} ${isFeatured ? "report-card-featured" : ""}`}>
      <div className="report-card-meta"><span>{isFeatured ? "FEATURED FREE" : report.category ?? "REPORT"}</span><span>{report.platforms[0]?.name ?? "Platform unavailable"}</span></div>
      <div className="report-card-title-row"><h3>{report.title}</h3><MarketSparkline history={report.marketHistory} /></div>
      <MarketContext report={report} />
      <div className="report-card-comparison">
        <div><span className="report-card-stat-label">MARKET</span><strong>{report.marketProbability === null || report.marketProbability === undefined ? "Unavailable" : `${Math.round(report.marketProbability * 100)}%`}</strong></div>
        <div><span className="report-card-stat-label">FTM ESTIMATE</span><strong className={locked ? "report-card-locked-value" : "report-card-brand-value"}>{ftmEstimateLabel}</strong></div>
        <div><span className="report-card-stat-label">DIVERGENCE</span><strong className="report-card-divergence-value">{divergenceLabel}</strong></div>
      </div>
      <ProbabilityDivergenceBar marketProbability={report.marketProbability} ftmEstimateLow={locked ? undefined : report.ftmEstimateLow} ftmEstimateHigh={locked ? undefined : report.ftmEstimateHigh} divergenceLow={report.divergenceLow} divergenceHigh={report.divergenceHigh} evidenceBalance={report.evidenceBalance} compact locked={locked} />
      <div className="report-card-footer"><div className="report-card-badges"><EvidenceBalanceBadge value={report.evidenceBalance} /><ConfidenceBadge value={report.confidence} /></div><span className="report-card-updated">{formatTimestamp(report.lastReviewedAt ?? null)}</span></div>
      {locked ? <p className="report-card-lock-copy">FTM Estimate locked · Pro unlocks all current reports and complete evidence packs.</p> : report.shortReasons.length ? <ul className="report-card-reasons">{report.shortReasons.slice(0, 2).map((reason) => <li key={reason}>{reason}</li>)}</ul> : null}
      <Link className="report-card-link" to={`/reports/${report.slug}`}>{locked ? "Unlock Report" : "View Report"}<span aria-hidden="true"> →</span></Link>
    </article>
  );
}
